/**
 * THE FLOATING PANEL SURFACE: one declaration of what a HUD over the chart looks like.
 *
 * <h2>Why this is a module and not two `const GLASS` objects</h2>
 *
 * It WAS two. `DynaManReplayOverlay` declared one at `rgba(8,12,18,0.72)` with a 10px blur, a
 * 0.10 border and no shadow; `DynaManDebugPanel` declared another at 0.93 with a 12px blur, a
 * 0.14 border and a shadow. The two panels sit side by side in the same docked column, so the
 * ledger and the engine state were visibly different surfaces — one of them with an edge and one
 * without — and nothing said they were meant to match.
 *
 * <p>That is the `#d4a017` failure this repository already records for the simulator palette:
 * three files holding the same hex under three comments each claiming it was shared. A surface
 * is edited by eye, one file at a time, which is exactly how two of them drift.
 *
 * <h2>What a glass panel is for</h2>
 *
 * It floats OVER the price action rather than beside it, so the chart keeps its full width and
 * the reader keeps the context the numbers are about. That only works if the panel is legible
 * against whatever happens to be behind it, which is what the three ingredients are for:
 *
 * <pre>
 *   a dark, semi-transparent fill   the candles show through as texture, not as content
 *   a backdrop blur                 a trendline underneath cannot be read as a digit
 *   a hairline border + shadow      the panel has an EDGE, so it reads as being in front
 * </pre>
 *
 * <p>The shadow is the one that was missing. Without it a translucent dark rectangle on a dark
 * chart has no boundary at all and reads as a hole in the canvas rather than as something
 * floating above it.
 */

/**
 * The fill. Semi-transparent so the chart is visible through it, dark so text is not.
 *
 * <h2>0.65, down from 0.85 — and the number came from a MEASUREMENT, not from taste</h2>
 *
 * At 0.85 the panel was a dark card that happened to admit a hint of the chart: the backdrop
 * contributed 15% of what a reader saw. At 0.65 it contributes 35%, so the price action the HUD
 * is describing is genuinely visible underneath it rather than nominally so.
 *
 * <h2>THIS CHART IS LIGHT-THEMED, which is what sets the floor</h2>
 *
 * The usual glassmorphism argument — "lower the alpha, the blur will keep the text readable" —
 * assumes a DARK backdrop, where the blur both diffuses structure and leaves the surface dark.
 * Here it does only the first. Sampled from the canvas under the dock's own rectangle (389x709
 * at the top right, 94.9% covered): the mean behind it is **#dedcdc** and the brightest pixel is
 * **pure white**. The blur removes the candle EDGES; it cannot remove the brightness, and only
 * the alpha darkens.
 *
 * <p>So the ceiling on transparency is a contrast floor. Composited over the worst case and
 * measured against {@link GLASS_TEXT}:
 *
 * <pre>
 *   alpha   over the mean   over WHITE   AA (4.5:1) for 11-12px text
 *   0.50       3.56:1         2.81:1     NO
 *   0.60       4.69:1         3.84:1     NO
 *   0.65       5.46:1         4.55:1     yes — and the most transparent that is
 *   0.72       6.69:1         5.75:1     yes
 *   0.85       9.88:1         9.15:1     yes (what it was)
 * </pre>
 *
 * <p>0.65 is therefore the most transparent this panel can be while its own numbers stay
 * readable. The 0.50 the brief suggested is two thirds of the way to illegible on a white
 * backdrop — it would be the right value against a dark chart, and this is not one. If the
 * chart ever gets a dark theme, this is one line and the table above says what it buys.
 *
 * <p>The blur still earns its place and is untouched: without it the lowered alpha would put
 * sharp candle edges and dotted band rays directly behind the digits. The two are a pair.
 */
export const GLASS_BACKGROUND = 'rgba(25, 28, 36, 0.65)';

/**
 * The denser fill, for a panel that is mostly small text.
 *
 * <p>The engine-state panel is a dozen rules at 12.5px and its own note explains why: "the
 * background is near-opaque so text is not competing with candles behind it". Kept as a VARIANT
 * of this surface rather than as a second surface, so the two panels differ in exactly one value
 * and share the border, the blur and the shadow.
 *
 * <h2>It came DOWN with the plain fill, from 0.93 to 0.80, and it had to</h2>
 *
 * The brief was about the ledger. But these two panels are STACKED IN THE SAME DOCK — measured
 * live, the ledger at y=145 and the engine state directly beneath it at y=864, both 389px wide
 * at x=2854 — and that adjacency is the entire reason this module exists. Leaving one at 0.65
 * and the other at 0.93 would recreate the divergence it was written to fix: one column, two
 * visibly different materials, which is what a reader notices rather than the alpha of either.
 *
 * <p>0.80 rather than 0.65, because the variant's whole purpose is that it is denser: a dozen
 * rules at 12.5px, where the plain panel is mostly large figures. It holds 7.70:1 over white
 * against the plain fill's 4.55:1 — comfortable rather than merely passing — and the step
 * between them is 0.15, which reads as the same glass in two weights.
 */
export const GLASS_BACKGROUND_DENSE = 'rgba(25, 28, 36, 0.80)';

/**
 * THE MANUAL OVERRIDE: the fills a reader gets when they ask for more glass than is safe.
 *
 * <h2>Why these exist at all, when the measurement says 0.65 is the floor</h2>
 *
 * The contrast table above is about the WORST case — the brightest pixel the dock can sit over,
 * with 11px text on top of it. A reader looking at a particular chart can see that their own
 * backdrop is nothing like that, and they are better placed than a constant to judge it. So the
 * override is offered, and it is OFF by default: the panel ships at the value that is legible
 * whatever is behind it, and the lighter one is something a reader turns on while looking at it.
 *
 * <p>0.50 is the value the brief named. Over pure white it composites to about #8c8e92 and the
 * near-white text holds **2.81:1**, which is below the 4.5:1 WCAG AA wants for body text — that
 * is the whole reason it is not the default, and the reason the contrast test exempts this pair
 * by name rather than by loosening the threshold for everything.
 *
 * <p>The DENSE override moves with it, 0.80 -> 0.65, for the same reason the two defaults move
 * together: the panels are stacked in one dock, and an override that lightened one of them would
 * split the surface in two again. The step stays 0.15, and the dense override is still the
 * default plain fill — so even at its lightest the engine-state panel clears AA.
 */
export const GLASS_BACKGROUND_TRANSPARENT = 'rgba(25, 28, 36, 0.50)';
export const GLASS_BACKGROUND_DENSE_TRANSPARENT = 'rgba(25, 28, 36, 0.65)';

/**
 * The blur behind the fill.
 *
 * <p>10px rather than the 4px that would do: this sits over candlesticks and dotted band rays,
 * and at 4px a ray crossing behind a row of digits is still a line the eye tries to read. More
 * blur is strictly better for the thing the blur is for, and it costs nothing — the browser
 * composites it either way.
 */
export const GLASS_BLUR = 'blur(10px)';

/** The hairline. Enough to bound the panel, not enough to become a frame. */
export const GLASS_BORDER = '1px solid rgba(255, 255, 255, 0.10)';

/** The lift. What makes it read as floating ABOVE the chart rather than cut into it. */
export const GLASS_SHADOW = '0 8px 28px rgba(0, 0, 0, 0.45)';

/** The text colour, near-white rather than white: a pure white on dark glass glares. */
export const GLASS_TEXT = '#e8eef6';

/**
 * The same text colour at a lower alpha, for a label beside a figure.
 *
 * <p>A FUNCTION rather than three more constants, and it exists because the alternative is what
 * every panel in the dock was already doing: retyping `rgba(232, 238, 246, 0.80)` by hand. Three
 * channels copied into four files is the `#d4a017` failure in its original form -- and here it
 * is worse than cosmetic, because the contrast floor this surface was measured against is a
 * floor for THIS colour. A panel that picks its own grey is picking its own contrast ratio.
 *
 * @param alpha 0..1. The full-strength colour is {@link GLASS_TEXT} itself.
 */
export function glassText(alpha = 1) {
  const r = parseInt(GLASS_TEXT.slice(1, 3), 16);
  const g = parseInt(GLASS_TEXT.slice(3, 5), 16);
  const b = parseInt(GLASS_TEXT.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The surface, as an MUI `sx` fragment.
 *
 * <p>`WebkitBackdropFilter` beside the standard property, because Safari still needs it and a
 * panel that silently loses its blur on one browser is a panel nobody notices is broken there.
 *
 * <p>The two flags pick from a 2x2 table rather than nesting conditionals, so every combination
 * is a named constant somebody chose and none is arithmetic done at the call site.
 *
 * @param dense       whether to use the denser fill — a panel of small text rather than figures
 * @param transparent the reader's manual override, off by default. Lowers BOTH fills, because
 *                    the panels share a dock and an override that lightened one would split the
 *                    surface in two again
 */
export function glassSurface({ dense = false, transparent = false } = {}) {
  const fill = transparent
    ? (dense ? GLASS_BACKGROUND_DENSE_TRANSPARENT : GLASS_BACKGROUND_TRANSPARENT)
    : (dense ? GLASS_BACKGROUND_DENSE : GLASS_BACKGROUND);
  return {
    background: fill,
    backdropFilter: GLASS_BLUR,
    WebkitBackdropFilter: GLASS_BLUR,
    border: GLASS_BORDER,
    boxShadow: GLASS_SHADOW,
    borderRadius: 2,
    color: GLASS_TEXT,
  };
}

export default glassSurface;
